import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../../common/colorable-component";
import { ContactLayerSchemeDefinition, ContactLayerSchemeName } from "./constants/contact-layer-types";
import { CONTACT_LAYER_SCHEME_CONFIG, CONTACT_LAYER_SCHEME_MAP } from "./constants/contact-layer-constants";
import pfx from "../../../utils/style-class-prefix-helper";
import { ContactLayerSchemeConfiguration } from "../../../constants/colors";
import { KeyIds } from "../../../constants/keys";
import { setCssVariable } from "../../../utils/css-variables";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-contact-layer",
  styleUrl: "contact-layer.scss",
  shadow: true
})
export class ContactLayer implements ColorableComponent<ContactLayerSchemeConfiguration<ContactLayerSchemeDefinition>> {
  schemeConfig: ContactLayerSchemeConfiguration<ContactLayerSchemeDefinition> = CONTACT_LAYER_SCHEME_CONFIG;
  schemeMap = CONTACT_LAYER_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ContactLayerSchemeDefinition;
  @Element() hostElement: HTMLEonUiContactLayerElement;

  /**
   * Raised when the contact layer has been closed via button.
   */
  @Event() contactLayerClose: EventEmitter;

  /**
   * Raised when the contact layer has been opened
   */
  @Event() contactLayerOpen: EventEmitter;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ContactLayerSchemeName;

  /**
   * if this property is true, the contact-layer window will be shown
   */
  @Prop({ mutable: true, reflect: true }) visible: boolean = false;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Contact-bar height used to calculate the min-height, should be set by contact-bar
   */
  @Prop({ reflect: true, mutable: true }) contactBarHeight: number;

  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.setContactLayerMinHeight();
    this.hostElement.visible = false;
  }

  componentWillLoad() {
    this.applyScheme();
    document.addEventListener("keydown", (e) => {
      this.closeContactLayerKeyboardEvent(e);
    });
  }

  componentDidLoad() {
    this.setCssVariables();
    this.setContactLayerMinHeight();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("visible")
  emitEventOnOpening() {
    if (this.visible) {
      this.contactLayerOpen.emit();
    }
  }

  private setContactLayerMinHeight = () => {
    // min-height = contact-bar height + 2*48px margin
    // timeout is used to get the contact-bar height value only after it will be set
    window.setTimeout(() => {
      setCssVariable(this.hostElement as HTMLElement, "--minHeight", `${this.contactBarHeight + 2 * 48}px`);
    }, 0);
  };

  private handleContactLayerCloseClick = () => {
    if (this.visible) {
      this.visible = false;
      this.contactLayerClose.emit();
    }
  };

  closeContactLayerKeyboardEvent(_e: KeyboardEvent) {
    if (_e.key === KeyIds.ESCAPE) {
      this.handleContactLayerCloseClick();
    }
  }

  render() {
    return (
      <Host>
        <div class={pfx({ ["layer-wrapper"]: true, ["visible"]: !!this.visible })}>
          <div class={pfx("closingX")}>
            <eon-ui-icon
              class={pfx("icon-closing-x")}
              onClick={() => this.handleContactLayerCloseClick()}
              name="close"
              aria-label="close"
            ></eon-ui-icon>
          </div>
          <div class={pfx({ ["layer-content-wrapper"]: true })}>
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
